import { useEffect, useMemo, useState } from "react";
import { authErrorMessage } from "../lib/authErrors";
import { trackAuth, setAnalyticsUser } from "../analytics";

type AuthModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAuthenticated: () => void;
  initialAuthError?: string | null;
  initialMode?: "signin" | "signup";
};

const AuthModal = ({
  isOpen,
  onClose,
  onAuthenticated,
  initialAuthError = null,
  initialMode = "signin",
}: AuthModalProps) => {
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleStatus, setGoogleStatus] = useState<
    "loading" | "ready" | "needs-creds" | "needs-api"
  >("loading");

  const getCsrfToken = async () => {
    const response = await fetch("/api/auth/csrf");
    if (!response.ok) throw new Error("Failed to get CSRF token");
    const json = (await response.json()) as { csrfToken?: string };
    if (!json.csrfToken) throw new Error("Missing CSRF token");
    return json.csrfToken;
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setError(initialAuthError ? authErrorMessage(initialAuthError) : null);
    setPassword("");
    setConfirmPassword("");

    const loadProviders = async () => {
      setGoogleStatus("loading");
      try {
        const response = await fetch("/api/auth/providers");
        if (!response.ok) {
          setGoogleStatus("needs-api");
          return;
        }
        const providers = (await response.json()) as Record<string, unknown>;
        setGoogleStatus(providers.google ? "ready" : "needs-creds");
      } catch {
        setGoogleStatus("needs-api");
      }
    };
    void loadProviders();
  }, [initialAuthError, initialMode, isOpen]);

  const canSubmit = useMemo(() => {
    if (!email.trim() || password.length < 6) return false;
    if (mode === "signup" && password !== confirmPassword) return false;
    return true;
  }, [confirmPassword, email, mode, password]);

  if (!isOpen) return null;

  const submitCredentials = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const csrfToken = await getCsrfToken();
      const form = new URLSearchParams();
      form.set("csrfToken", csrfToken);
      form.set("email", email.trim());
      form.set("password", password);
      form.set("callbackUrl", window.location.href);
      form.set("json", "true");

      const response = await fetch("/api/auth/callback/credentials", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: form.toString(),
      });

      const json = (await response.json()) as { url?: string };
      setIsSubmitting(false);
      if (!response.ok || !json.url || json.url.includes("error=")) {
        setError(
          mode === "signup"
            ? "Could not create account with this email/password."
            : "Invalid email or password.",
        );
        return;
      }

      // Track sign-up vs sign-in at the moment the action actually happens.
      // Also mark this session so TopHeader's refreshSession doesn't double-fire.
      const trimmedEmail = email.trim();
      setAnalyticsUser(trimmedEmail);
      if (mode === "signup") {
        trackAuth("sign_up", trimmedEmail);
        // Clear session tracker so the subsequent sign_in from refreshSession is also suppressed
        sessionStorage.setItem("analytics_session_tracked", trimmedEmail);
      } else {
        trackAuth("sign_in", trimmedEmail);
        sessionStorage.setItem("analytics_session_tracked", trimmedEmail);
      }

      onAuthenticated();
      onClose();
      window.location.href = json.url;
    } catch {
      setIsSubmitting(false);
      setError(
        mode === "signup"
          ? "Could not create account with this email/password."
          : "Invalid email or password.",
      );
    }
  };

  const continueWithGoogle = async () => {
    try {
      const csrfToken = await getCsrfToken();
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/api/auth/signin/google";
      form.style.display = "none";

      const csrfInput = document.createElement("input");
      csrfInput.name = "csrfToken";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      const callbackInput = document.createElement("input");
      callbackInput.name = "callbackUrl";
      callbackInput.value = window.location.href;
      form.appendChild(callbackInput);

      document.body.appendChild(form);
      form.submit();
    } catch {
      setError("Failed to start Google sign in. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl font-bold">
            {mode === "signin" ? "Sign In" : "Create Account"}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">
            ✕
          </button>
        </div>

        <div className="flex bg-gray-100 rounded-full p-1 mb-5">
          <button
            type="button"
            onClick={() => setMode("signin")}
            className={`flex-1 rounded-full py-2 text-sm font-medium transition ${
              mode === "signin" ? "bg-white shadow" : "text-gray-600"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setMode("signup")}
            className={`flex-1 rounded-full py-2 text-sm font-medium transition ${
              mode === "signup" ? "bg-white shadow" : "text-gray-600"
            }`}
          >
            Sign Up
          </button>
        </div>

        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Email address"
            className="w-full border rounded-xl px-4 py-3"
          />
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password (min 6 characters)"
            className="w-full border rounded-xl px-4 py-3"
          />
          {mode === "signup" && (
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Confirm password"
              className="w-full border rounded-xl px-4 py-3"
            />
          )}
        </div>

        {mode === "signup" && password && confirmPassword && password !== confirmPassword && (
          <p className="text-red-600 text-sm mt-2">Passwords do not match.</p>
        )}
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}

        <button
          type="button"
          disabled={!canSubmit || isSubmitting}
          onClick={submitCredentials}
          className="w-full mt-4 px-5 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-50"
        >
          {isSubmitting
            ? "Please wait..."
            : mode === "signin"
              ? "Sign In with Email"
              : "Create Account"}
        </button>

        <div className="my-4 flex items-center gap-2 text-gray-500 text-sm">
          <div className="h-px bg-gray-200 flex-1" />
          <span>or</span>
          <div className="h-px bg-gray-200 flex-1" />
        </div>

        <button
          type="button"
          disabled={googleStatus !== "ready"}
          onClick={continueWithGoogle}
          className="w-full px-5 py-3 border rounded-xl hover:bg-gray-50 disabled:opacity-50"
        >
          Continue with Google
        </button>
        {googleStatus === "needs-api" && (
          <p className="text-xs text-gray-500 mt-2">
            Auth server is not reachable. Restart dev with <code>npm run dev</code> from the repo
            root (or <code>npm run dev --prefix frontend</code>) and use{" "}
            <code>http://localhost:5173</code>.
          </p>
        )}
        {googleStatus === "needs-creds" && (
          <p className="text-xs text-gray-500 mt-2">
            Add Google OAuth credentials to <code>frontend/.env.local</code> and restart the auth
            server.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
